import cx from 'classnames'
import { UnitLabel } from '@/shared/config/config'
import LocalizedNumberFlow from '@/shared/components/ui/LocalizedNumberFlow'
import { useTranslation } from 'react-i18next'
import { CardHeader, CardTitle } from '@/common'
import {
  TriangleExclamationIcon,
  CheckHalfDashedCircleIcon,
  CheckIcon
} from '@/common/components/icons'

function StatusText({
  value,
  variant,
  warningText
}: {
  value: number
  variant?: 'default' | 'warning'
  warningText?: string
}) {
  const { t } = useTranslation()
  if (variant === 'warning') {
    return <span className={cx('text-xs text-text-secondary shrink-0')}>{warningText}</span>
  }

  if (value === 0) {
    return (
      <span className={cx('text-xs text-text-secondary shrink-0')}>{t('report:notStarted')}</span>
    )
  }
  if (value === 100) {
    return (
      <span className={cx('text-xs text-text-secondary shrink-0')}>{t('report:completed')}</span>
    )
  }

  return (
    <span className={cx('text-xs text-text-secondary shrink-0')}>{`${value.toFixed(0)}%`}</span>
  )
}

export function SectionCompletionProgressBar({
  value,
  maxValue,
  isCompleted,
  variant,
  warningText
}: {
  value: number
  maxValue: number
  isCompleted: boolean
  variant?: 'default' | 'warning'
  warningText?: string
}) {
  const isPending = value > 0 && value < 100
  const percentage = (value / maxValue) * 100
  const barClass = cx('h-2', {
    'bg-green-500': percentage === 100,
    'bg-yellow-500': isPending && variant === 'default',
    'bg-blue-500': percentage === 0
  })

  return (
    <div className="flex items-center gap-2 min-w-24">
      <div className={cx('w-full bg-gray-400 h-2 shrink-0')}>
        <div className={barClass} style={{ width: `${percentage}%` }} />
      </div>

      <div className="flex items-center gap-2">
        <span className={cx('text-xs text-text-secondary shrink-0')}>
          <StatusText value={value} variant={variant} warningText={warningText} />
        </span>
        {isCompleted && (
          <div className="text-green-600 flex items-center gap-1">
            <CheckIcon className="size-5" />
          </div>
        )}
        {isPending && (
          <div className={cx('flex items-center gap-1', 'text-yellow-500')}>
            <CheckHalfDashedCircleIcon className="size-5" />
          </div>
        )}
        {variant === 'warning' && (
          <div className={cx('flex items-center gap-1', 'text-yellow-500')}>
            <TriangleExclamationIcon className="size-5" />
          </div>
        )}
      </div>
    </div>
  )
}

export function ReportSectionCardHeader({
  title,
  editButton,
  isOptional = false,
  icon,
  emissions,
  completionPercentage = 0,
  variant = 'default',
  warningText
}: {
  title: string
  editButton?: React.ReactNode
  isOptional?: boolean
  icon?: React.ReactNode
  emissions?: number
  completionPercentage?: number
  variant?: 'default' | 'warning'
  warningText?: string
}) {
  const { t } = useTranslation()
  const isCompleted = completionPercentage === 100
  return (
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle
          title={
            <div className="flex items-center gap-2">
              {icon}
              {title}
              {isOptional && (
                <small className="font-normal text-text-secondary">
                  {t('report:common:optional')}
                </small>
              )}
              <div className="ml-4">
                <SectionCompletionProgressBar
                  value={completionPercentage}
                  maxValue={100}
                  isCompleted={isCompleted}
                  variant={variant}
                  warningText={warningText}
                />
              </div>
            </div>
          }
        />
        <div className="flex items-center gap-2">
          {(emissions || emissions === 0) && (
            <div>
              <LocalizedNumberFlow value={emissions} /> {UnitLabel.tCO2e}
            </div>
          )}
          {editButton}
        </div>
      </div>
    </CardHeader>
  )
}
